<template>
  <van-dialog class="hk-dialog"
              ref="dialog"
              :data-exercise-id="currentExercise.id"
              :is-disable-close="true"
              :is-show-footer="true">
    <template slot="title">
      <van-quizzes-toolbar :title="courseData.navigation" :is-forced="false" @close="close" />
    </template>
    <div class="hk-dialog-wrap" ref="wrap">
      <div class="ready" v-if="isSceneReady">
        <h1>{{ courseData.title }}</h1>
        <br />
        <h2>{{ courseData.subtitle }}</h2>
        <p class="description">
          <template v-if="isLearning">{{ courseData.description }}</template>
          <template v-else>{{ courseData.description }}</template>
        </p>
        <br />
        <div v-if="exercises.length">
          <el-button type="primary" @click="scene = 2">
            <template v-if="isLearning">开始学习</template>
            <template v-else-if="isExam">开始考试</template>
            <template v-else>开始练习</template>
          </el-button>
          <el-button type="danger" v-if="type === 'section'" @click="close">其他练习</el-button>
        </div>
      </div>
      <template v-else-if="isSceneError">
        <div class="flex-wrap">
          <p class="large">{{errorInfo.message}}</p>
          <br />
          <div>
            <el-button type="danger" round @click="close">退出</el-button>
          </div>
        </div>
      </template>
      <template v-else-if="isScenePlaying">
        <van-quizzes-controller
            ref="controller"
            :exercises="exercises"
            :is-show-explain="isShowExplain"
            :is-exam="isExam"
            :is-backtrack="isBacktrack"
            @exercise-change="exerciseChange"
            @result="updateResult"
            @change="change"
            @submit="examFinish"
            @end="finish" />
      </template>
      <template v-else-if="isSceneExamList && isShow">
        <van-exam-list :exercises="exercises"
                       :user-answer="results"
                       ref="examList"/>
        <van-answer-card v-show="isShowAnswerCard"
                         :score="score"
                         :total="exercises.length"
                         :is-exam="isExam"
                         :is-learning-exam="isLearningExam"
                         :is-review="isReview"
                         :results="results"
                         :title="courseData.title"
                         @again-exam="$emit('continue')"
                         @to="setAnchor"/>
      </template>
      <div class="finished" v-else>
        <div class="flex-wrap" v-if="isReview && !isExam">
          <p class="large">你真棒！完成一组学习啦！</p>
          <br />
          <div>
            <el-button type="danger" round @click="continueLearning">继续</el-button>
          </div>
        </div>
      </div>
    </div>
    <div slot="footer">
      <van-footer-bar
          :current-exercise="currentExercise"
          :is-exam="isExam"
          :is-show-answer-card="isShowAnswerCard"
          :total="exercises.length"
          :current="exerciseIndex"
          :scene="scene"
          :results="results"
          @submit="handleSubmitExamBtn"
          @detail="detail"
          @prev="prev"
          @next="next"
          @back="examListShow" />
    </div>
    <van-answer-card v-show="isScenePlaying && isShowAnswerCard"
                     v-if="isExam"
                     @to="selectExercise"
                     :total="exercises.length"
                     :results="results" />
  </van-dialog>
</template>

<script>
import { Button } from 'element-ui';
import utils from '@/services/utils';

import VanDialog from '@/components/common/Dialog.vue';
import VanQuizzesToolbar from '@/components/quizzes/QuizzesToolbar.vue';
import VanQuizzesController from '@/components/quizzes/hk/Quizzes.vue';
import VanFooterBar from '@/components/common/FooterBar.vue';
import VanAnswerCard from '@/components/common/AnswerCard.vue';
import VanExamList from '@/components/quizzes/hk/ExamDetail.vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { COURSE_DATA, SUBMIT_COURSE_RESULT, CANCEL_LAST_TIME_LEARNED } from '@/store/learning/types';
import { CURRENT_EXERCISE } from '@/store/hk/types';

const SCENE = Object.freeze({
  ready: 1,
  playing: 2,
  finished: 3,
  examList: 4,
  error: 5,
});

export default {
  props: {
    isExam: {
      type: Boolean,
      default: false,
    },
    isLearningExam: {
      type: Boolean,
      default: false,
    },
    isExamRecords: {
      type: Boolean,
      default: false,
    },
    examRecordScore: {
      type: Number,
      default: 0,
    },
    status: {
      type: Number,
      default: 0,
    },
  },
  components: {
    ElButton: Button,
    VanQuizzesToolbar,
    VanDialog,
    VanQuizzesController,
    VanFooterBar,
    VanExamList,
    VanAnswerCard,
  },
  data() {
    return {
      scene: SCENE.ready,
      score: 0,
      results: [],
      exerciseIndex: 0,
      isShowAnswerCard: false,
      latestChange: null,
      isComplete: false,
      errorInfo: {},
      isShow: false,
    };
  },
  computed: {
    ...mapGetters({
      courseData: COURSE_DATA,
      currentExercise: CURRENT_EXERCISE,
    }),
    exercises() {
      const { quizzes } = this.courseData;
      if (quizzes && quizzes.length) {
        return quizzes.map(item => ({
          ...item,
          userAnswer: item.wrongAnswer ? JSON.parse(item.wrongAnswer) : null,
          isAgain: !!item.wrongAnswer,
        }));
      }
      return [];
    },
    answeredList() {
      return this.courseData.answeredList || [];
    },
    type() {
      return this.courseData.type;
    },
    courseId() {
      return this.$route.params.courseId || 0;
    },
    assessmentId() {
      return this.courseData.id;
    },
    isNoData() { // 不产生数据的游戏code
      return this.exercises.every(exercise => exercise.code === 'ZSD' || exercise.code === 'XT');
    },
    isBacktrack() {
      return this.courseData.isBacktrack;
    },
    isReview() {
      return this.courseData.type === 'review';
    },
    isLearning() { // 小节学习
      return this.courseData.type === 'learning';
    },
    isPractice() { // 小节练习
      return this.courseData.type === 'practice';
    },
    isWrongBook() {
      return this.courseData.type === 'wrong';
    },
    isSceneReady() {
      return this.scene === SCENE.ready;
    },
    isSceneError() {
      return this.scene === SCENE.error;
    },
    isScenePlaying() {
      return this.scene === SCENE.playing;
    },
    isSceneExamList() {
      return this.scene === SCENE.examList;
    },
    isFirstExercise() {
      return this.exerciseIndex <= 0;
    },
    isLastExercise() {
      return this.exerciseIndex >= this.exercises.length - 1;
    },
    isBQ() {
      return this.currentExercise.code === 'BQ';
    },
    isDX() {
      return this.currentExercise.code === 'DX';
    },
    isAutoSubmit() {
      return (this.isLearning || this.isPractice || this.isWrongBook)
        && this.results.length;
    },
    isMidwaySave() { // 试卷中途保存
      return this.isExam && !this.isExamRecords && this.results.length;
    },
    isShowExplain() {
      return !this.isLearning;
    },
  },
  watch: {
    currentExercise() {
      if (this.outputAnswer) {
        this.outputAnswer();
      }
    },
  },
  mounted() {
    this.disableCopy();
    this.outputAnswer = utils.debounce(() => {
      let finalAnswer;
      if (this.currentExercise) {
        if (!this.isDX && this.currentExercise.text) {
          finalAnswer = (this.currentExercise.text
            .match(/(\[\[[^\]]*]])/g) || []).map(item => item.replace(/[[|\]]/g, ''));
        } else {
          const { answer } = this.currentExercise;
          finalAnswer = answer;
        }
        /* eslint-disable-next-line */
        if (window['whosyourdaddy']) {
          utils.log(finalAnswer);
        }
      }
    }, 400);
  },
  methods: {
    ...mapMutations({
      cancelLastTimeLearned: CANCEL_LAST_TIME_LEARNED,
    }),
    ...mapActions({
      submitCourseResult: SUBMIT_COURSE_RESULT,
    }),
    disableCopy() {
      const el = document.querySelector('.hk-dialog');
      el.oncontextmenu = (e) => {
        e.preventDefault();
      };

      el.onselectstart = (e) => {
        e.preventDefault();
      };

      el.oncopy = (e) => {
        e.preventDefault();
      };

      el.onkeydown = e => !(e.ctrlKey || e.ctrlKey || e.shiftKey);
    },
    setAnchor(index) {
      this.$refs.examList.setAnchor(index);
      this.isShowAnswerCard = false;
    },
    examListShow() {
      this.isShowAnswerCard = true;
    },
    open() {
      this.isShow = true;
      this.reset();
      if (this.isExamRecords) {
        this.score = this.examRecordScore;
        this.reset(SCENE.examList);
        this.isShowAnswerCard = true;
        this.results = this.initExamUserResult();
      }
      this.bindEnterEvent();
      if (this.isBacktrack) {
        utils.confirm('欢迎回来，要继续从上一次退出的地方开始考吗？', '提示', {
          showClose: false,
          closeOnClickModal: false,
        })
          .then(() => {
            this.backtrack();
            this.scene = SCENE.playing;
            this.isShowAnswerCard = true;
            this.$refs.dialog.open();
          }, () => {
            this.cancelLastTimeLearned();
            this.$refs.dialog.open();
          });
        return;
      }
      this.$refs.dialog.open();
    },
    close() {
      // this.isShow = false;
      this.unBindEnterEvent();
      if (this.isExamRecords) {
        this.scene = SCENE.ready;
      }

      if (this.isScenePlaying) {
        if (this.isAutoSubmit) {
          this.$refs.dialog.close();
          this.submitResult({
            seconds: this.$refs.controller.seconds,
            results: this.results,
          }, () => {
            this.$emit('close');
          });
        } else if (this.isMidwaySave) {
          utils.confirm('确定要退出考试吗？系统会保持您当前的进度，以便下次回来可以继续。', '提示', {
            showClose: false,
            closeOnClickModal: false,
          })
            .then(() => {
              this.submitResult({
                seconds: this.$refs.controller.seconds,
                results: this.results,
                isBacktrack: true,
              }, () => {
                this.$refs.dialog.close();
                this.$emit('close');
              });
            }, () => {
              utils.noop();
            });
        } else {
          this.$refs.dialog.close();
          this.$emit('close');
        }
      } else {
        this.$refs.dialog.close();
        this.$emit('close');
      }
    },
    error(error) {
      this.errorInfo = error;
      this.reset(SCENE.error);
    },
    reset(scene) {
      this.exerciseIndex = 0;
      this.results = [];
      this.isComplete = false;
      this.isShowAnswerCard = false;
      this.scene = scene || SCENE.ready;
    },
    backtrack() {
      this.results = this.exercises.map((exercise) => {
        const answerIndex = utils.Array.findIndex(this.answeredList, exercise, 'id');
        let answer = exercise.code === 'BQ' ? [] : -1;
        let isComplete = false;
        let isRight = false;
        if (answerIndex !== -1) {
          const answered = this.answeredList[answerIndex];
          if (exercise.code === 'BQ') {
            answer = JSON.parse(answered.answer);
            const rightAnswer = exercise.text.match(/(?<=\[\[)[^\]]*(?=]])/g) || [];
            isRight = rightAnswer.every((item, index) => item === answer[index]);
            isComplete = answer.length === rightAnswer.length && answer.every(item => item);
          } else if (exercise.code === 'DX') {
            answer = Number(answered.answer);
            isRight = exercise.answerIndex === answer;
            isComplete = true;
          }
        }
        exercise.userAnswer = answer;
        exercise.isComplete = isComplete;
        exercise.isRight = isRight;
        return {
          answer,
          code: exercise.code,
          id: exercise.id,
          isComplete,
          isRight,
        };
      });
    },
    continueLearning() {
      this.reset();
      this.$emit('continue');
    },
    finish({ seconds }) {
      this.scene = SCENE.finished;
      this.submitResult({
        seconds,
        results: this.results,
      }, () => {
        if (this.isReview) return;
        this.reset();
        this.$emit('continue');
      });
    },
    examFinish({ results, seconds }) {
      this.scene = SCENE.finished;
      this.submitResult({
        seconds,
        results,
      }, ({ score }) => {
        this.score = score;
        this.scene = SCENE.examList;
        this.isShowAnswerCard = true;
      });
    },
    submitResult({ seconds, results, isBacktrack }, callback) {
      if (this.isNoData) {
        results = this.exercises.map(exercise => ({
          id: exercise.id,
          isRight: true,
          answer: '',
        }));
      }
      this.submitCourseResult({
        id: this.assessmentId,
        courseId: this.courseId,
        type: (this.isLearning || this.isPractice) ? 'review' : this.type,
        seconds,
        isBacktrack,
        quizzes: results.map(exerciseAnswer => ({
          id: exerciseAnswer.id,
          isRight: exerciseAnswer.isRight,
          answer: utils.isObject(exerciseAnswer.answer)
            ? JSON.stringify(exerciseAnswer.answer) : exerciseAnswer.answer,
        })),
      })
        .then((res) => {
          /* eslint-disable no-unused-expressions */
          callback && callback(res);
          this.$emit('update-progress');
        });
    },
    updateResult({ results }) {
      this.results = results.filter(result => (result.code === 'BQ' || result.code === 'DX'));
    },
    exerciseChange(index) {
      // when isExam is true 生效
      this.exerciseIndex = index;
    },
    change(payload) {
      this.isComplete = payload.isComplete;
    },
    selectExercise(index) {
      this.$refs.controller.toggleExerciseIndex(index);
      this.isShowAnswerCard = !this.isShowAnswerCard;
    },
    handleSubmitExamBtn() {
      this.$refs.controller.submit();
    },
    detail() {
      this.isShowAnswerCard = !this.isShowAnswerCard;
    },
    prev() {
      if (!this.isExam || this.isFirstExercise) return;
      this.$refs.controller.prev();
    },
    next() {
      /* eslint-disable no-lonely-if */
      if (this.isExam) {
        if (this.isLastExercise) return;
      } else {
        if (this.isDX && !this.currentExercise.isComplete) return;
      }
      this.$refs.controller.next();
      this.$nextTick(() => {
        this.$refs.wrap.scrollTop = 0;
      });
    },
    handleGlobalEnter(event) {
      if (!this.isScenePlaying) return;
      if (this.isShowAnswerCard) return;
      if (event.keyCode === 13) {
        if (this.isBQ && !this.isComplete) return;
        this.next();
      }
    },
    bindEnterEvent() {
      document.addEventListener('keyup', this.handleGlobalEnter);
    },
    unBindEnterEvent() {
      document.removeEventListener('keyup', this.handleGlobalEnter);
    },
    initExamUserResult() {
      /* eslint-disable no-useless-escape */
      return this.exercises.map((item) => {
        let answer = '';
        const isRight = item.wrongAnswer === '';
        if (item.code === 'DX') {
          if (isRight) {
            answer = item.answerIndex;
          } else {
            answer = item.wrongAnswer * 1;
          }
        } else if (item.code === 'BQ') {
          if (isRight) {
            answer = item.text.match(/\[\[[^\[]*\]\]/g)
              .map(strItem => strItem.replace(/^\[\[/g, '').replace(/\]\]$/g, ''));
            /* eslint-disable */
            for (let i = 0; i < answer.length; i += 1) {
              const item = utils.toCDB(answer[i].trim())
                .replace(/[[\]]/g, '')
                .replace(/\s/g, '')
                .replace(/#\d+#/g, '').split('||');
              answer[i] = item[0];
            }
          } else {
            answer = JSON.parse(item.wrongAnswer);
          }
        }
        return {
          answer,
          code: item.code,
          id: item.id,
          isComplete: true,
          isRight,
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/styles/variables.scss';

  .hk-dialog {
    .hk-dialog-wrap {
      height: 490px;
      overflow-y: auto;
      .ready, .flex-wrap {
        height: 100%;
        text-align: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        h1 {
          padding: 0 20px;
          line-height: 1.8;
        }
        .large {
          font-size: 24px;
        }
        .el-button {
          min-width: 120px;
        }
      }
      .finished {
        height: 100%;
      }
    }
     /deep/ .el-dialog {
      .el-dialog__body {
        .content {
          padding: 0;
        }
      }
      .el-dialog__footer {
        padding: 5px 20px;
        border: 0;
        .dialog-footer {
          min-height: 55px;
        }
      }
    }
  }
</style>
